import { useState } from 'react';
import { balanceAsOf, isInvestmentAccount, type InvestmentMove, type Month } from '../../data/types';
import { useApp } from '../context';
import { useAsync } from '../hooks';
import { Empty, Panel, Stat } from '../ui';
import { SoFar } from '../SoFar';
import { MoveForm } from '../MoveForm';
import { useSealedMoney } from '../sealed';
import { LedgerStrip, takeSealed, useWide } from '../ledger';
import { categoryCuts, isMaterialCut, monthVerdict, reserve, RESERVE_LATER_MONTHS, RESERVE_TARGET_MONTHS } from '../../lib/decisions';
import { closeTargetMonth, latestPublished, monthKey, previousPublished, publishedMonths } from '../../lib/periods';
import { DASH, dateLabel, deltaLabel, monthLabel, naira, nairaShort } from '../../lib/format';
import { navigate } from '../../lib/route';

const monthName = (start: string) => new Date(`${start}T00:00:00`).toLocaleDateString('en-GB', { month: 'long' });

/** The one sentence Home opens with, and the next step it implies. */
function Hero({ latest, target, targetInReview, moves, onMoved }: {
  latest: Month | null; target: string | null; targetInReview: boolean; moves: InvestmentMove[] | null; onMoved: () => void;
}) {
  const { repo, base } = useApp();
  const sealed = useSealedMoney(latest);
  const p1 = latest ? previousPublished(base.months, latest.month_start) : null;
  const p2 = p1 ? previousPublished(base.months, p1.month_start) : null;
  const cuts = useAsync(async () => {
    if (!latest) return [];
    const [cur, ...priors] = await Promise.all([latest, p1, p2].filter((m): m is Month => Boolean(m)).map((m) => repo.categoryMonths(m.id)));
    return categoryCuts(cur!, priors).filter(isMaterialCut);
  }, [latest?.id]);
  const [moving, setMoving] = useState(false);
  const m = sealed.data?.money ?? null;
  const verdict = m ? monthVerdict(m) : null;
  // The real figure, shown at once: never an interim amount that is not true.
  const shown = (verdict?.kind === 'left' ? verdict.amount : verdict?.kind === 'overspent' ? verdict.amount : verdict?.kind === 'investedBeyond' ? verdict.spare : null);
  const name = latest ? monthName(latest.month_start) : null;
  const fig = (v: number | null, bad = false) => <span className={`hero-figure ${bad ? 'bad' : 'good'}`}>{nairaShort(v)}</span>;
  const movedTotal = latest && moves ? moves.filter((x) => x.from_month === latest.month_start).reduce((a, x) => a + x.amount, 0) : 0;
  const toMove = m?.leftover && m.leftover > 0 ? Math.max(0, m.leftover - movedTotal) : 0;
  const lever = cuts.data?.[0];

  let line: JSX.Element;
  let note: string;
  if (!latest) {
    line = <>Close your first month to see what it left you.</>;
    note = 'Every figure here comes from statements you close and publish.';
  } else if (!m) {
    line = <>Reading {name}…</>;
    note = '';
  } else {
    const v = verdict!;
    if (v.kind === 'unknown') {
      line = <>{name} is sealed.</>;
      note = `No income was recorded for ${name}, so what was left to move is unknown. Check that salary landed in an account whose statement was uploaded.`;
    } else if (v.kind === 'left') {
      line = <>{name} left you {fig(shown)} to move.</>;
      note = movedTotal > 0
        ? toMove > 0 ? `You marked ${nairaShort(movedTotal)} as moved. ${nairaShort(toMove)} is still in your bank accounts.` : `You marked all of it as moved. The bank alert or next statement confirms it.`
        : 'After spending, Migrate World and investing. It is still in your bank accounts; moving it to GT Money Market counts it as invested.';
    } else if (v.kind === 'investedBeyond') {
      line = <>{name} left {fig(shown)} spare, and you invested {nairaShort(v.invested)}.</>;
      note = `${nairaShort(v.fromSavings)} of that investing came from savings. It is still yours, now in investments.`;
    } else {
      line = <>{name} spent {fig(shown, true)} more than it earned.</>;
      note = `Spending and Migrate World came to more than income.${v.invested > 0 ? ` Separately, you moved ${nairaShort(v.invested)} into investments; that is still yours.` : ''}`;
    }
  }

  const closing = target && <button className={toMove > 0 ? 'secondary' : 'cta'} onClick={() => navigate({ section: 'close' })}>
    {targetInReview ? `Continue closing ${monthLabel(target)}` : `Close ${monthLabel(target)}`}</button>;

  return (
    <section className="hero" aria-live="polite">
      <h1 className="hero-line">{line}</h1>
      {note && <p className="hero-note">{note}</p>}
      {lever && (
        <button className="lever" onClick={() => navigate({ section: 'insights', month: monthKey(latest!.month_start) })}>
          <span>Biggest lever</span>
          {lever.name} {lever.overCap >= lever.aboveAvg ? `ran ${nairaShort(lever.overCap)} over its ceiling` : `ran ${nairaShort(lever.aboveAvg)} above its average`}
        </button>
      )}
      <div className="hero-actions">
        {toMove > 0 && moves !== null && <button className="cta" onClick={() => setMoving(true)}>I moved {nairaShort(toMove)}</button>}
        {closing}
        {latest && <button className="link" onClick={() => navigate({ section: 'insights', month: monthKey(latest.month_start) })}>See the {name} decision</button>}
      </div>
      {moving && latest && <MoveForm fromMonth={latest.month_start} amount={toMove} onClose={(saved) => { setMoving(false); if (saved) onMoved(); }} />}
    </section>
  );
}

export function Home() {
  const { repo, base } = useApp();
  const { months, openPlan, accounts } = base;
  const wide = useWide();
  const [stamp] = useState(takeSealed);
  const moves = useAsync(() => repo.investmentMoves().catch(() => null), []);
  const latest = latestPublished(months);
  const prior = latest ? previousPublished(months, latest.month_start) : null;
  const target = closeTargetMonth(months);
  const targetRow = target ? months.find((m) => m.month_start === target) : null;

  const ngn = accounts.filter((a) => a.currency === 'NGN' && a.current_balance !== null);
  const bank = ngn.filter((a) => !isInvestmentAccount(a));
  const mm = ngn.filter((a) => (a.account_type ?? '').toLowerCase() === 'money_market');
  const total = (xs: typeof ngn) => (xs.length ? xs.reduce((s, a) => s + (a.current_balance ?? 0), 0) : null);
  const asOf = (xs: typeof ngn) => xs.map(balanceAsOf).sort()[0];
  const r = reserve(accounts, publishedMonths(months));
  const pct = r.monthsCovered === null ? 0 : Math.min(100, (r.monthsCovered / RESERVE_TARGET_MONTHS) * 100);
  const reserveTone = r.monthsCovered === null ? '' : r.monthsCovered >= RESERVE_TARGET_MONTHS ? 'within' : 'watch';
  const pending = (moves.data ?? []).filter((x) => x.moved_on >= (latest?.published_at ?? '').slice(0, 10));

  const change = (label: string, key: 'personal_spend_actual' | 'lifestyle_spend_actual' | 'investment_actual') => {
    const d = latest && prior ? deltaLabel(latest[key], prior[key]) : null;
    const arrow = d ? (d.direction === 'up' ? '↑' : d.direction === 'down' ? '↓' : '') : '';
    return (
      <Stat
        label={label}
        value={latest ? nairaShort(latest[key]) : DASH}
        foot={d ? <span className="delta">{arrow} {d.text} than {monthName(prior!.month_start)}</span> : 'Needs two sealed months'}
        onClick={latest ? () => navigate({ section: 'spend', month: monthKey(latest.month_start) }) : undefined}
      />
    );
  };

  const primary = (
    <>
      <Panel title={latest ? `${monthName(latest.month_start)}, sealed` : 'Last month'}>
        {latest ? <div className="stats-3">{change('Personal spend', 'personal_spend_actual')}{change('Lifestyle', 'lifestyle_spend_actual')}{change('Invested', 'investment_actual')}</div>
          : <Empty>Nothing is sealed yet. Close a month to compare.</Empty>}
      </Panel>
      <Panel title="Reserve" meta={reserveTone ? <span className={`tag tag-${reserveTone}`}>{reserveTone === 'within' ? 'On target' : 'Below target'}</span> : undefined}>
        <p className="reserve-line"><b>{r.monthsCovered === null ? DASH : r.monthsCovered.toFixed(1)}</b> of {RESERVE_TARGET_MONTHS} months of spending covered</p>
        <span className="meter" role="img" aria-label={`Reserve ${r.monthsCovered === null ? 'unknown' : `${r.monthsCovered.toFixed(1)} of ${RESERVE_TARGET_MONTHS} months`}`}>
          <i style={{ width: `${pct}%`, background: reserveTone === 'within' ? 'var(--within)' : 'var(--watch)' }} />
        </span>
        <ul className="ledger reserve-ledger">
          <li><span>Bank accounts<small>{bank.length ? `as of ${dateLabel(asOf(bank))}` : 'none recorded'}</small></span><b>{naira(total(bank))}</b></li>
          <li><span>GT Money Market<small>{mm.length ? `as of ${dateLabel(asOf(mm))}` : 'none recorded'}</small></span><b>{naira(total(mm))}</b></li>
          <li className="ledger-total"><span>Reserve<small>{r.avgMonthlySpend ? `at ${nairaShort(r.avgMonthlySpend)} average monthly spend` : 'needs a sealed month'}</small></span><b>{naira(r.liquid)}</b></li>
        </ul>
        {r.stale.length > 0 && <p className="muted small">Not current: {r.stale.map((x) => `${x.name} (last updated ${dateLabel(x.asOf)})`).join(', ')}.</p>}
        {pending.length > 0 && <p className="muted small">Marked moved since the last close: {nairaShort(pending.reduce((a, x) => a + x.amount, 0))} to {pending[0]!.destination}. Balances update when the statement arrives.</p>}
        <p className="muted small">Last recorded balances, not live. Moving bank money to GT Money Market keeps the reserve the same and counts as invested. Next goal after {RESERVE_TARGET_MONTHS} months: {RESERVE_LATER_MONTHS}.</p>
      </Panel>
    </>
  );

  const secondary = (
    <>
      {openPlan ? <SoFar plan={openPlan} /> : (
        <Panel title="Plan" meta="No actuals until it is closed"><Empty>No open month yet.</Empty></Panel>
      )}
      {latest?.monthly_brief && (
        <Panel title="Brief" meta={monthName(latest.month_start)}>
          <p className="brief">{latest.monthly_brief}</p>
          {latest.next_best_action && <p className="next">{latest.next_best_action}</p>}
        </Panel>
      )}
    </>
  );

  return (
    <div className="stack">
      <LedgerStrip months={months} openStart={openPlan?.month_start} stamp={stamp}
        onSelect={(k) => navigate({ section: 'spend', month: k })} label="Your months" />
      {stamp && <p className="sealed-note">{monthLabel(`${stamp}-01`)} is sealed. It can no longer change.</p>}
      <Hero latest={latest} target={target} targetInReview={targetRow?.status === 'review'} moves={moves.data ?? null} onMoved={moves.reload} />
      {wide
        ? <div className="panes"><div className="stack">{primary}</div><div className="stack">{secondary}</div></div>
        : <>{primary}{secondary}</>}
    </div>
  );
}
